import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { url } from "./url";
import { v4 as uuidv4 } from "uuid";
import {
  ensureNotificationPermission,
  getNotificationPermissionState,
  PERMISSION_STATE,
} from "./pwa/permissions";
import { getMarankooServiceWorkerRegistration } from "./pwa/serviceWorker";

const firebaseConfig = {
  apiKey: "AIzaSyBiRLaeZ5Q5HU42ZeOy0gA_Q8AFOhm7KKI",
  authDomain: "marankoo.firebaseapp.com",
  projectId: "marankoo",
  storageBucket: "marankoo.firebasestorage.app",
  messagingSenderId: "848954995043",
  appId: "1:848954995043:web:68826db126e9cf54c72356",
  measurementId: "G-YRC5CYXD1P"
};

const VAPID_KEY = "BPdB31VGGPIunYCJfGQf9PK95x_3-v2H3kTX6gclVfWlhv12ZHJiwomg-87sZ3xk0d3en8B5GCRnV2Jn-Chximc";

const app = initializeApp(firebaseConfig);
let messagingInstancePromise = null;

const getDeviceId = () => {
  // Browser subscription and native FCM are distinct devices/tokens.
  const storageKey = Capacitor.isNativePlatform() ? "marankoo_native_device_id" : "marankoo_device_id";
  let deviceId = localStorage.getItem(storageKey);

  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(storageKey, deviceId);
  }

  return deviceId;
};

async function getMessagingInstance() {
  if (messagingInstancePromise) return messagingInstancePromise;

  messagingInstancePromise = isSupported()
    .then((supported) => (supported ? getMessaging(app) : null))
    .catch(() => null);

  return messagingInstancePromise;
}

async function savePushToken(pushToken, accessToken) {
  await axios.post(
    `${url}/fcm/v1/save-push-token-passenger`,
    {
      push_token: pushToken,
      device_id: getDeviceId(),
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

}

// Native: register a listener for each registration attempt, then dispose it.
// No web Firebase SDK/service worker is used inside the Android WebView.
let nativeRegistrationPromise = null;
async function readNotificationPermissionState() {
  if (!Capacitor.isNativePlatform()) return getNotificationPermissionState();
  try {
    return (await PushNotifications.checkPermissions()).receive;
  } catch (error) {
    console.error("Native notification permission check failed:", error);
    return PERMISSION_STATE.UNSUPPORTED;
  }
}

async function requestNativeToken(accessToken, requestPermission) {
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive !== "granted" && requestPermission &&
      (permission.receive === "prompt" || permission.receive === "prompt-with-rationale")) {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== "granted") return "";
  if (nativeRegistrationPromise) return nativeRegistrationPromise;

  nativeRegistrationPromise = (async () => {
    const handles = [];
    let timeoutId;
    try {
      const token = await new Promise(async (resolve, reject) => {
        try {
          handles.push(await PushNotifications.addListener("registration", (result) => resolve(result.value)));
          handles.push(await PushNotifications.addListener("registrationError", (result) => reject(new Error(String(result.error)))));
          timeoutId = setTimeout(() => reject(new Error("FCM registration timed out")), 20000);
          await PushNotifications.register();
        } catch (error) {
          reject(error);
        }
      });
      if (!token) return "";
      // A logout during registration must not register a stale account's token.
      if (localStorage.getItem("accessToken") !== accessToken) return "";
      await savePushToken(token, accessToken);
      return token;
    } finally {
      clearTimeout(timeoutId);
      await Promise.all(handles.map((handle) => handle.remove()));
    }
  })();
  try {
    return await nativeRegistrationPromise;
  } finally {
    nativeRegistrationPromise = null;
  }
}

async function requestForToken(explicitAccessToken, { requestPermission = false } = {}) {
  const accessToken = explicitAccessToken || localStorage.getItem("accessToken");

  // Backend token storage is authenticated; guests should not call it.
  if (!accessToken) return "";

  try {
    if (Capacitor.isNativePlatform()) {
      return await requestNativeToken(accessToken, requestPermission);
    }
    const permission = await ensureNotificationPermission({ request: requestPermission });
    if (permission !== PERMISSION_STATE.GRANTED) return "";

    const messaging = await getMessagingInstance();
    if (!messaging) return "";

    const registration = await getMarankooServiceWorkerRegistration();
    if (!registration) return "";

    // Keep getToken for now because the current backend contract stores push_token.
    const pushToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!pushToken) return "";

    await savePushToken(pushToken, accessToken);
    return pushToken;
  } catch (err) {
    // Push notifications are optional and must never break login/app bootstrap.
    console.error("Push notification registration failed:", err);
    return "";
  }
}

async function subscribeToForegroundMessages(callback) {
  if (Capacitor.isNativePlatform()) {
    const handle = await PushNotifications.addListener("pushNotificationReceived", (notification) => {
      callback({ notification, data: notification.data || {} });
    });
    return () => { handle.remove(); };
  }
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};

  return onMessage(messaging, callback);
}

const onMessageListener = async () => {
  if (Capacitor.isNativePlatform()) {
    return new Promise((resolve, reject) => {
      let handlePromise;
      handlePromise = PushNotifications.addListener("pushNotificationReceived", (notification) => {
        resolve({ notification, data: notification.data || {} });
        handlePromise.then((handle) => handle.remove());
      });
      handlePromise.catch(reject);
    });
  }
  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  return new Promise((resolve) => {
    onMessage(messaging, (payload) => resolve(payload));
  });
};

export {
  requestForToken,
  onMessageListener,
  subscribeToForegroundMessages,
  getNotificationPermissionState,
  readNotificationPermissionState,
};
